<template>
  <div
    class="psd2-container grow"
    :class="{
      'is-business': isBusiness,
      'p-2': !isBusiness && source === PSD2_FIELD_SOURCE.BNPL_APPLICATION,
    }"
  >
    <Psd2Flow :flow="psd2" />
  </div>

  <Psd2ActionArea
    :flow="psd2"
    :class="{ 'business-action-area': isBusiness }"
  />
</template>

<script setup>
/**
 * The PSD2 sub-flow, wherever it is embedded.
 *
 * This was two components, one per segment, ~90% identical, plus a profile
 * object whose job was to carry five facts between them. Four of those five
 * were the same fact - whether the subject of the sync is a company - and the
 * fifth was a token namespace, which is now one function.
 */
import { PSD2_FIELD_SOURCE } from "~/utils/const";
import { usePsd2Flow } from "~/composables/usePsd2Flow";

/**
 * `done` and `back` leave PSD2: the page that mounted these screens moves its
 * own flow, because only it knows what surrounds them. A mount that offers no
 * way out simply does not listen.
 */
const emit = defineEmits(["synced", "done", "back"]);

const props = defineProps({
  /**
   * Whether the subject of the sync is a company. Decides which aggregator
   * endpoints we reach, which token namespace the copy comes from, and whether
   * the screens draw titles of their own.
   */
  isBusiness: {
    type: Boolean,
    required: false,
    default: false,
  },
  /**
   * Who this sync is for. The business endpoints want the subject named, and
   * which of the three names it goes by is the mount site's answer: an
   * application or a prescore by its own id, a standalone limit by its company.
   */
  publicId: {
    type: String,
    required: false,
  },
  prescoreId: {
    type: String,
    required: false,
  },
  companyId: {
    type: [String, Number],
    required: false,
  },
  correctionId: {
    type: Number,
    required: false,
  },
  correctionCreatedAt: {
    type: Date,
    required: false,
  },
  fieldId: {
    type: Number,
    required: false,
  },
  skippable: {
    type: Boolean,
    required: false,
    default: false,
  },
  source: {
    type: String,
    required: false,
    default: PSD2_FIELD_SOURCE.BNPL_APPLICATION,
  },
  /**
   * The flow whose configuration decides how long an earlier sync stays valid.
   * Only the A8 aggregator asks for it.
   */
  flowId: {
    type: Number,
    required: false,
    default: null,
  },
  /**
   * Whether these screens draw their own footer. False where the page draws one
   * for its whole flow and reads `actionArea` off this component instead.
   */
  ownsActionArea: {
    type: Boolean,
    required: false,
    default: true,
  },
  /** Whether the first screen may offer a way back out of PSD2 altogether. */
  canLeaveBackwards: {
    type: Boolean,
    required: false,
    default: false,
  },
  /** Whether the later screens may offer a way back to the previous one. */
  canStepBackwards: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// Deliberately not destructured: see usePsd2Flow.
const psd2 = usePsd2Flow(props, emit);

/**
 * The b2b application and prescore pages draw one footer for their whole flow.
 * They read this rather than working out a second time which buttons a PSD2
 * screen wants - which is how the two answers used to drift apart.
 */
defineExpose({
  actionArea: computed(() => psd2.actionArea),
  next: psd2.next,
  back: psd2.back,
});
</script>

<style lang="scss" scoped>
.psd2-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 32px;
  font-size: 12px;

  &.is-business {
    gap: 24px;
  }
}

.business-action-area {
  padding: 64px 0 0;
}
</style>
